'use client';

/* eslint-disable @next/next/no-img-element */
import type { MessageSource } from '@/type/chat';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ReferenceCardProps {
  sourceId?: string;
  sources?: MessageSource[];
}

const HIDE_DELAY = 160;
const CARD_WIDTH = 412;
const VIEWPORT_PADDING = 10;

export default function ReferenceCard({ sourceId, sources = [] }: ReferenceCardProps) {
  const normalizedSources = useMemo(() => sources.filter(Boolean).map(normalizeSource), [sources]);
  const primarySource = useMemo(
    () => (sourceId ? normalizedSources.find((source) => source.id === sourceId) : undefined),
    [sourceId, normalizedSources],
  );

  const sourceUrl = primarySource?.url || '';
  const siteName = primarySource?.label || getSiteName(sourceUrl) || 'Source';
  const sourceDescription = primarySource?.description || primarySource?.publishTime || sourceUrl;
  const [isOpen, setIsOpen] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showCard = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    setIsOpen(true);
  };

  const hideCard = () => {
    hideTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, HIDE_DELAY);
  };

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const cardHeight = cardRef.current?.getBoundingClientRect().height || 204;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cardWidth = Math.min(CARD_WIDTH, viewportWidth - VIEWPORT_PADDING * 2);
    const scrollY = window.scrollY;
    let top = triggerRect.top + scrollY - cardHeight - 10;
    let left = triggerRect.left + triggerRect.width / 2 - cardWidth / 2;

    if (triggerRect.top < cardHeight + 12 && viewportHeight - triggerRect.bottom > cardHeight) {
      top = triggerRect.bottom + scrollY + 10;
    }

    left = Math.max(VIEWPORT_PADDING, Math.min(left, viewportWidth - cardWidth - VIEWPORT_PADDING));
    setCardPosition({ top, left });
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  if (!primarySource) return null;

  return (
    <>
      <a
        ref={triggerRef}
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mx-1 truncate inline-flex h-5 max-w-45 items-center rounded-sm bg-white px-2.5 text-[12px] font-medium leading-none text-[#202124]! no-underline! shadow-xs align-baseline"
        onMouseEnter={showCard}
        onMouseLeave={hideCard}
      >
        {siteName}
      </a>

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={cardRef}
            className="absolute z-9999 w-[min(412px,calc(100vw-20px))]"
            style={{ top: cardPosition.top, left: cardPosition.left }}
            onMouseEnter={showCard}
            onMouseLeave={hideCard}
          >
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-[18px] border border-white/10 bg-[#1E1F24] px-6 py-5 text-white shadow-2xl no-underline"
            >
              <div className="line-clamp-2 text-[21px] font-semibold leading-[28px]">
                {primarySource.title || siteName}
              </div>
              <div className="mt-3 line-clamp-3 text-base leading-[24px] text-white/45">
                {sourceDescription}
              </div>
              <div className="mt-6 flex items-center gap-2 text-base text-white/70">
                <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10">
                  <img
                    src={`https://www.google.com/s2/favicons?domain_url=${encodeURIComponent(
                      sourceUrl,
                    )}&sz=64`}
                    alt=""
                    className="size-6"
                  />
                </span>
                <span className="truncate">{siteName}</span>
              </div>
            </a>
          </div>,
          document.body,
        )}
    </>
  );
}

interface NormalizedReferenceSource {
  id: string;
  title: string;
  label: string;
  description: string;
  url: string;
  publishTime?: string;
}

function normalizeSource(source: MessageSource): NormalizedReferenceSource {
  const url = source.source_url || source.url || '';
  const label = source.source || getSiteName(url) || 'Source';

  return {
    id: source.ref_source_id || source.id || '',
    title: source.title || label,
    label,
    description: source.snippet || '',
    url,
    publishTime: source.publish_time,
  };
}

function getSiteName(url: string) {
  try {
    const { hostname } = new URL(url);
    const domain = hostname.replace(/^www\./i, '');
    return domain.replace(/\.(com|org|net|io|ai|co|dev|app|xyz|info|biz|tv|me|cc)$/i, '');
  } catch {
    return url.replace(/.+\/\/|www\.|\..+/g, '');
  }
}
